import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { useLanguage } from "../context/LanguageContext";

export function ChatWidget() {
  const { t, language, dir } = useLanguage();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  const panelRef = useRef(null);
  const launcherRef = useRef(null);

  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{ role: "assistant", text: t.chatbot.greeting }]);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e) => {
      if (panelRef.current?.contains(e.target) || launcherRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [open]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, sending]);

  const send = async (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || sending) return;
    const next = [...messages, { role: "user", text }];
    setMessages(next);
    setInput("");
    setSending(true);
    try {
      // Backend keeps this scoped to EXOPILOT questions only (system prompt) and
      // proxies to the LLM so no API key is ever exposed to the browser -- see
      // POST /api/chat in cloud-backend/server.py. Returns a friendly
      // "not configured" reply until OPENAI_API_KEY is set server-side.
      const { data } = await api.post("/chat", {
        messages: next.map((m) => ({ role: m.role, content: m.text })),
        language,
      });
      setMessages((cur) => [...cur, { role: "assistant", text: data.reply }]);
    } catch (err) {
      // The backend returns a normal 200 with a "not configured" reply when
      // OPENAI_API_KEY isn't set yet -- reaching this catch means the request
      // itself failed (network, 5xx, etc.), a genuinely different state.
      setMessages((cur) => [...cur, { role: "assistant", text: t.chatbot.error }]);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <button
        ref={launcherRef}
        data-testid="chat-widget-launcher"
        onClick={() => setOpen((o) => !o)}
        aria-label={t.chatbot.launcherLabel}
        className={`fixed bottom-4 sm:bottom-6 ${dir === "rtl" ? "left-4 sm:left-6" : "right-4 sm:right-6"} z-40 w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-[#111] dark:bg-white text-white dark:text-[#111] shadow-xl flex items-center justify-center hover:scale-105 transition-transform`}
      >
        {open ? <X className="w-5 h-5" /> : <MessageCircle className="w-5 h-5" />}
      </button>

      {open && (
        <div ref={panelRef} data-testid="chat-widget-panel"
          // h-[min(...)] caps the panel to whatever's smaller: its normal 28rem height,
          // or the viewport minus room for the launcher/header -- without this a short
          // mobile browser (address bar visible, landscape, etc.) could clip the bottom
          // of the panel off-screen.
          className={`fixed bottom-20 sm:bottom-24 ${dir === "rtl" ? "left-4 sm:left-6" : "right-4 sm:right-6"} z-40 w-[calc(100vw-2rem)] sm:w-[calc(100vw-3rem)] max-w-sm h-[min(28rem,calc(100vh-7rem))] bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-lg shadow-2xl flex flex-col overflow-hidden`}
        >
          <div className="px-5 py-4 border-b border-neutral-100 dark:border-neutral-800 bg-[#111] dark:bg-neutral-950 text-white">
            <p className="text-sm font-medium">{t.chatbot.title}</p>
            <p className="text-[11px] text-neutral-400 mt-0.5">{t.chatbot.subtitle}</p>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] rounded-lg px-3.5 py-2.5 text-sm leading-relaxed ${
                  m.role === "user"
                    ? "bg-[#111] dark:bg-white text-white dark:text-[#111]"
                    : "bg-neutral-100 dark:bg-neutral-800 text-neutral-700 dark:text-neutral-200"
                }`}>
                  {m.text}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="bg-neutral-100 dark:bg-neutral-800 rounded-lg px-3.5 py-2.5">
                  <Loader2 className="w-4 h-4 animate-spin text-neutral-400" />
                </div>
              </div>
            )}
          </div>

          <form onSubmit={send} className="p-3 border-t border-neutral-100 dark:border-neutral-800 flex items-center gap-2">
            <input data-testid="chat-widget-input" value={input} onChange={(e) => setInput(e.target.value)} placeholder={t.chatbot.placeholder}
              className="flex-1 min-w-0 bg-neutral-50 dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-full px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#111] dark:focus:ring-white" />
            <button type="submit" data-testid="chat-widget-send-button" disabled={!input.trim() || sending} aria-label={t.chatbot.send}
              className="w-9 h-9 shrink-0 rounded-full bg-[#111] dark:bg-white text-white dark:text-[#111] flex items-center justify-center disabled:opacity-40 transition-opacity">
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
